/**
 * Dialog de finalização 'Faturar — {contrato}': resumo do consumo, datas,
 * desconto/juros com 'Valor Real' recalculado ao vivo, NFe e observações.
 */
import { useState, type FormEvent } from 'react';
import type { InvoiceCreateBody, PendingBilling } from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Dialog } from '@/components/ui/misc';
import { errorMessage } from './billing-queries';
import { useCreateInvoice } from './billing-mutations';
import {
  centsToMoney,
  formatBRL,
  formatBRLInput,
  formatCompetence,
  minutesToHHMM,
  moneyToCents,
  parseBRLInput,
  todayISO,
} from './billing-format';

function MoneyField({
  id,
  label,
  value,
  onChange,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const invalid = parseBRLInput(value) === null;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <span className="pointer-events-none absolute top-1/2 left-3 -translate-y-1/2 text-sm text-muted-foreground">
          R$
        </span>
        <Input
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={() => {
            const parsed = parseBRLInput(value);
            if (parsed !== null) onChange(formatBRLInput(parsed));
          }}
          inputMode="decimal"
          aria-invalid={invalid || undefined}
          className="pl-9 text-right tabular-nums"
        />
      </div>
      {invalid ? <p className="text-xs text-destructive">Valor inválido.</p> : null}
    </div>
  );
}

export function InvoiceCreateDialog({
  pending,
  onCreated,
  onClose,
}: {
  pending: PendingBilling;
  onCreated: () => void;
  onClose: () => void;
}) {
  const [billingDate, setBillingDate] = useState(todayISO());
  const [dueDate, setDueDate] = useState(pending.suggestedDueDate);
  const [discountText, setDiscountText] = useState('0,00');
  const [interestText, setInterestText] = useState('0,00');
  const [nfeNumber, setNfeNumber] = useState('');
  const [observations, setObservations] = useState(
    `Contrato ${pending.contractName} — competência ${formatCompetence(pending.competence)}`
  );

  const create = useCreateInvoice();

  const discount = parseBRLInput(discountText);
  const interest = parseBRLInput(interestText);
  const baseCents = moneyToCents(pending.baseValue);
  const realValue =
    discount !== null && interest !== null
      ? centsToMoney(baseCents - moneyToCents(discount) + moneyToCents(interest))
      : null;

  const valid = billingDate !== '' && dueDate !== '' && discount !== null && interest !== null;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!valid || discount === null || interest === null) return;
    const body: InvoiceCreateBody = {
      contractId: pending.contractId,
      competence: pending.competence,
      billingDate,
      dueDate,
      discountValue: discount,
      interestValue: interest,
      nfeNumber: nfeNumber.trim() || null,
      observations: observations.trim() || null,
    };
    create.mutate(body, { onSuccess: onCreated });
  };

  const { consumption } = pending;

  return (
    <Dialog open onClose={onClose} title={`Faturar — ${pending.contractName}`}>
      <form onSubmit={submit} className="space-y-4">
        {/* Resumo do consumo */}
        <div className="rounded-md bg-muted p-3 text-sm">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <span className="font-medium">{pending.clientName}</span>
            <span className="text-[13px] text-muted-foreground">
              Competência {formatCompetence(pending.competence)}
            </span>
          </div>
          {consumption.workedMinutes != null && consumption.includedMinutes != null ? (
            <div className="mt-1 tabular-nums">
              <span className="font-semibold">{minutesToHHMM(consumption.workedMinutes)}</span>
              <span className="text-muted-foreground">
                {' '}
                / {minutesToHHMM(consumption.includedMinutes)} incluídas
              </span>
            </div>
          ) : null}
          <p className="mt-1 text-[13px] text-muted-foreground">{consumption.summary}</p>
          <div className="mt-2 border-t border-divider pt-2 text-[13px] text-muted-foreground">
            Valor base{' '}
            <span className="font-semibold text-foreground tabular-nums">
              {formatBRL(pending.baseValue)}
            </span>
          </div>
        </div>

        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="invoice-billing-date">
              Data de faturamento <span className="text-destructive">*</span>
            </Label>
            <Input
              id="invoice-billing-date"
              type="date"
              value={billingDate}
              onChange={(e) => setBillingDate(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="invoice-due-date">
              Data de vencimento <span className="text-destructive">*</span>
            </Label>
            <Input
              id="invoice-due-date"
              type="date"
              value={dueDate}
              onChange={(e) => setDueDate(e.target.value)}
              required
            />
          </div>
          <MoneyField
            id="invoice-discount"
            label="Valor desconto"
            value={discountText}
            onChange={setDiscountText}
          />
          <MoneyField
            id="invoice-interest"
            label="Valor juros"
            value={interestText}
            onChange={setInterestText}
          />
        </div>

        {/* Valor Real — recalculado ao vivo */}
        <div className="flex items-center justify-between rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5">
          <span className="text-sm font-medium">Valor Real</span>
          <span className="text-base font-semibold tabular-nums">
            {realValue !== null ? formatBRL(realValue) : '—'}
          </span>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="invoice-nfe">Número NFe</Label>
          <Input
            id="invoice-nfe"
            value={nfeNumber}
            onChange={(e) => setNfeNumber(e.target.value)}
            placeholder="Opcional"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="invoice-observations">Observações</Label>
          <Textarea
            id="invoice-observations"
            value={observations}
            onChange={(e) => setObservations(e.target.value)}
          />
        </div>

        {create.error ? (
          <p className="text-sm text-destructive">{errorMessage(create.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={create.isPending || !valid}>
            {create.isPending ? 'Faturando…' : 'Faturar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
